/** Plain-language copy and starter recipes for the Automations UI. */

import { findPiece } from './automationPieces';

export const AUTOMATION_STEPS_EXPLAINER = [
  {
    step: '1',
    title: 'Pick a trigger — “When…”',
    body: 'Choose what starts the automation. Example: a new lead lands in CRM, a form is submitted, or an outside app hits your webhook URL.',
  },
  {
    step: '2',
    title: 'Add actions — “Then…”',
    body: 'Add steps in order: send email, notify Slack, save to CRM, wait a few seconds, or only continue if a condition matches.',
  },
  {
    step: '3',
    title: 'Test, then turn Live',
    body: 'Use Test run with sample data. When it looks right, switch to Live so real events (new leads, webhooks, etc.) run it automatically.',
  },
];

export const WHAT_YOU_CAN_BUILD = [
  'New scraped lead → welcome email',
  'High-score lead → Slack alert for sales',
  'Form submit → create CRM lead + thank-you email',
  'Deal won → internal celebration message',
  'Webhook from Zapier → update CRM',
  'AI agent drafts outreach → email sends it (advanced)',
  'Custom event → ask an agent → message your team',
];

export const PLACEHOLDER_HINTS = [
  { token: '{{trigger.data.email}}', meaning: 'Email from the event (lead, form, webhook)' },
  { token: '{{trigger.data.first_name}}', meaning: 'First name' },
  { token: '{{trigger.data.company}}', meaning: 'Company name' },
  { token: '{{steps.0.response.output}}', meaning: 'Text returned by step #1 (e.g. AI agent)' },
];

export const BASE_SMART_FIELDS = [
  { label: 'Lead email', token: '{{trigger.data.email}}' },
  { label: 'First name', token: '{{trigger.data.first_name}}' },
  { label: 'Company', token: '{{trigger.data.company}}' },
  { label: 'Phone', token: '{{trigger.data.phone}}' },
  { label: 'Source', token: '{{trigger.data.source}}' },
  { label: 'Notes', token: '{{trigger.data.notes}}' },
];

export function smartFieldsForStep(stepIndex = 0) {
  const fields = [...BASE_SMART_FIELDS];
  for (let i = 0; i < stepIndex; i += 1) {
    fields.push({ label: `Output from step ${i + 1}`, token: `{{steps.${i}.response.output}}` });
  }
  return fields;
}

/** Starter workflows — one click to load into the editor. */
export const AUTOMATION_RECIPES = [
  {
    id: 'welcome-email',
    emoji: '✉️',
    title: 'Welcome new leads',
    when: 'A lead is added to CRM',
    then: 'Send a friendly welcome email',
    trigger: { type: 'crm.lead.created', config: {} },
    steps: [
      {
        type: 'send_email',
        config: {
          to: '{{trigger.data.email}}',
          subject: 'Thanks for connecting, {{trigger.data.first_name}}!',
          body: 'Hi {{trigger.data.first_name}},\n\nGreat to meet you. We received your details for {{trigger.data.company}} and will be in touch soon.\n\n- The team',
        },
      },
    ],
  },
  {
    id: 'lead-slack',
    emoji: '💬',
    title: 'Slack alert for new leads',
    when: 'A lead is added to CRM',
    then: 'Post a message in Slack',
    trigger: { type: 'crm.lead.created', config: {} },
    steps: [
      {
        type: 'slack_notify',
        config: {
          text: 'New lead: {{trigger.data.first_name}} at {{trigger.data.company}} ({{trigger.data.email}})',
        },
      },
    ],
  },
  {
    id: 'webhook-to-crm',
    emoji: '🔗',
    title: 'Webhook → save to CRM',
    when: 'Another app sends data to your webhook URL',
    then: 'Create a CRM lead from that data',
    trigger: { type: 'webhook', config: {} },
    steps: [
      {
        type: 'crm_lead_create',
        config: {
          email: '{{trigger.data.email}}',
          first_name: '{{trigger.data.first_name}}',
          company: '{{trigger.data.company}}',
          source: 'webhook',
        },
      },
    ],
  },
  {
    id: 'custom-ai-triage',
    emoji: '🤖',
    title: 'Custom event → ask AI',
    when: 'Any custom event hits your webhook',
    then: 'Ask an AI Studio agent, then message Slack',
    trigger: { type: 'custom_event', config: { event_name: 'custom_event' } },
    steps: [
      {
        type: 'run_agent',
        config: {
          input: 'Read this event and summarize what happened. Event data: {{trigger.data.company}} {{trigger.data.email}} {{trigger.data.notes}}',
        },
      },
      {
        type: 'slack_notify',
        config: {
          text: 'AI summary for custom event: {{steps.0.response.output}}',
        },
      },
    ],
  },
  {
    id: 'ai-lead-email',
    emoji: '🧠',
    title: 'AI writes lead outreach',
    when: 'A lead is added to CRM',
    then: 'Ask an agent to write the message, then send email',
    trigger: { type: 'crm.lead.created', config: {} },
    steps: [
      {
        type: 'run_agent',
        config: {
          input: 'Write a short, friendly outreach email for {{trigger.data.company}}. Lead email: {{trigger.data.email}}. Notes: {{trigger.data.notes}}',
        },
      },
      {
        type: 'send_email',
        config: {
          to: '{{trigger.data.email}}',
          subject: 'Quick idea for {{trigger.data.company}}',
          body: '{{steps.0.response.output}}',
        },
      },
    ],
  },
  {
    id: 'agent-team-review',
    emoji: '👥',
    title: 'Two AI agents work together',
    when: 'A lead is added to CRM',
    then: 'Qualifier agent reviews, writer agent drafts, Slack gets summary',
    trigger: { type: 'crm.lead.created', config: {} },
    steps: [
      {
        type: 'run_agent',
        config: {
          input: 'You are the lead qualifier. Score this lead from 1-100 and explain why. Company: {{trigger.data.company}} Email: {{trigger.data.email}} Notes: {{trigger.data.notes}}',
        },
      },
      {
        type: 'run_agent',
        config: {
          input: 'You are the outreach writer. Use this qualifier analysis to write a short outreach email: {{steps.0.response.output}}',
        },
      },
      {
        type: 'slack_notify',
        config: {
          text: 'Agent team finished. Qualifier: {{steps.0.response.output}}\n\nDraft: {{steps.1.response.output}}',
        },
      },
    ],
  },
  {
    id: 'lead-filter-email',
    emoji: '🎯',
    title: 'Only email scraped leads',
    when: 'A lead is added to CRM',
    then: 'If source is lead scraper → send email',
    trigger: { type: 'crm.lead.created', config: {} },
    steps: [
      {
        type: 'filter',
        config: {
          left: '{{trigger.data.source}}',
          op: 'equals',
          right: 'lead_scraper',
        },
      },
      {
        type: 'send_email',
        config: {
          to: '{{trigger.data.email}}',
          subject: 'Quick idea for {{trigger.data.company}}',
          body: 'Hi — we noticed {{trigger.data.company}} and wanted to share a quick idea. Reply if you would like to chat.',
        },
      },
    ],
  },
];

export function buildFlowSummary(workflow) {
  if (!workflow) return '';
  const triggerType = workflow.trigger?.type;
  const trigger = triggerType ? findPiece(triggerType) : null;
  const when = trigger?.simpleLabel || trigger?.label || (triggerType ? triggerType : 'Nothing yet — pick a trigger');
  const actions = (workflow.steps || [])
    .map((s, i) => {
      const p = findPiece(s.type);
      return p?.simpleLabel || p?.label || `Step ${i + 1}`;
    })
    .filter(Boolean);
  if (!actions.length) return `When “${when}” → (add at least one action)`;
  return `When “${when}” → ${actions.join(' → ')}`;
}

export function recipeToWorkflow(recipe, existingId) {
  const steps = (recipe.steps || []).map((s, i) => ({
    id: crypto.randomUUID(),
    type: s.type,
    order: i,
    config: { ...(s.config || {}) },
  }));
  return {
    id: existingId,
    name: recipe.title,
    status: 'inactive',
    trigger: { ...recipe.trigger },
    steps,
  };
}

export function humanRunStatus(status) {
  const map = {
    ok: 'Success — all steps completed',
    error: 'Failed — a step errored (expand for details)',
    filtered: 'Stopped — filter condition was not met',
  };
  return map[status] || status;
}

export function humanStepStatus(status) {
  const map = {
    ok: 'Done',
    error: 'Failed',
    skipped: 'Skipped',
    filtered: 'Stopped (filter)',
    queued: 'Queued to send later',
    pending_approval: 'Waiting for your approval to send',
  };
  return map[status] || status;
}
